/**
 * Post-login redirect targets come from untrusted input (`?next=`, the Kakao
 * `redirectPath`, the OAuth state cookie), so they are reduced to strict
 * same-origin relative paths before being used in a redirect.
 *
 * Isomorphic: safe to use in route handlers and in the browser.
 */
export const DEFAULT_REDIRECT_PATH = "/";

const MAX_REDIRECT_LENGTH = 2048;

/**
 * Code point ranges that URL parsers strip, normalize or treat specially:
 * C0/C1 control characters (tabs and newlines are silently removed by the
 * WHATWG parser, so `/<tab>/evil.com` becomes `//evil.com`), DEL, and
 * invisible Unicode spaces / format characters.
 */
const UNSAFE_CODE_POINTS: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x001f], // C0 controls (incl. tab, CR, LF)
  [0x007f, 0x009f], // DEL + C1 controls
  [0x00a0, 0x00a0], // no-break space
  [0x00ad, 0x00ad], // soft hyphen
  [0x1680, 0x1680], // ogham space mark
  [0x180e, 0x180e], // mongolian vowel separator
  [0x2000, 0x200f], // typographic spaces, zero-width chars, LRM/RLM
  [0x2028, 0x202f], // line/paragraph separators, bidi embeddings
  [0x205f, 0x206f], // math space, word joiner, invisible operators
  [0x3000, 0x3000], // ideographic space
  [0xfeff, 0xfeff], // byte order mark
];

/** Percent-encoded ASCII byte, e.g. `%2F` or `%5c`. */
const ENCODED_ASCII = /%([0-7][0-9a-f])/gi;

/** Redirecting into these after sign-in is never useful and can loop. */
const BLOCKED_PREFIXES = ["/api", "/auth"];

const PARSE_BASE = "https://redirect.invalid";

function hasUnsafeCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;

    if (
      UNSAFE_CODE_POINTS.some(
        ([min, max]) => codePoint >= min && codePoint <= max,
      )
    ) {
      return true;
    }
  }

  return false;
}

function hasSafeShape(path: string): boolean {
  return (
    path.startsWith("/") &&
    !path.startsWith("//") &&
    !path.includes("\\") &&
    !hasUnsafeCharacter(path)
  );
}

/**
 * Decode percent-encoded ASCII only. Multi-byte UTF-8 sequences (Korean or
 * Japanese search terms, say) can never produce `/`, `\` or control
 * characters, so they are left alone, and a stray `%` cannot throw.
 */
function decodeAscii(value: string): string {
  return value.replace(ENCODED_ASCII, (_match, hex: string) =>
    String.fromCharCode(Number.parseInt(hex, 16)),
  );
}

function isBlocked(pathname: string): boolean {
  const lower = pathname.toLowerCase();

  return BLOCKED_PREFIXES.some(
    (prefix) => lower === prefix || lower.startsWith(`${prefix}/`),
  );
}

/**
 * Return `value` if it is a safe same-origin path (one leading slash, no
 * scheme or host, no backslashes, control characters or encoded variants of
 * them), normalized by the URL parser; otherwise return `fallback`.
 *
 * @example
 * sanitizeRedirectPath("/en/movies?sort=imdb") // "/en/movies?sort=imdb"
 * sanitizeRedirectPath("//evil.com")           // "/"
 * sanitizeRedirectPath("/%5Cevil.com")         // "/"
 */
export function sanitizeRedirectPath(
  value: unknown,
  fallback: string = DEFAULT_REDIRECT_PATH,
): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > MAX_REDIRECT_LENGTH
  ) {
    return fallback;
  }

  // Validate the raw value and every decoded layer, so double-encoded tricks
  // such as `/%252F%252Fevil.com` are caught as well.
  let layer = value;

  for (let depth = 0; ; depth++) {
    if (!hasSafeShape(layer)) return fallback;

    const decoded = decodeAscii(layer);

    if (decoded === layer) break;
    if (depth >= 4) return fallback;
    layer = decoded;
  }

  let url: URL;

  try {
    url = new URL(value, PARSE_BASE);
  } catch {
    return fallback;
  }

  if (url.origin !== PARSE_BASE) return fallback;

  // Dot segments are resolved during parsing (`/..//evil.com` becomes
  // `//evil.com`), so the normalized result is checked again.
  const normalized = `${url.pathname}${url.search}${url.hash}`;

  if (!hasSafeShape(normalized) || isBlocked(decodeAscii(url.pathname))) {
    return fallback;
  }

  return normalized;
}

/**
 * Build an absolute redirect URL on `origin` for an untrusted path.
 *
 * @example buildSafeRedirectUrl("https://cagemdb.com", "//evil.com") // "https://cagemdb.com/"
 */
export function buildSafeRedirectUrl(
  origin: string,
  path: unknown,
  fallback: string = DEFAULT_REDIRECT_PATH,
): URL {
  return new URL(sanitizeRedirectPath(path, fallback), origin);
}
